import { ReactNode, useState } from "react";

import { Alert, AlertColor, Button, CircularProgress, Stack, Typography } from "@mui/material";

import { Trans, useTranslation } from "@/translations";

import { MailMergeAccessState } from "./MailMergeAccess.types";
import { MailMergeManageDialog } from "./MailMergeManageDialog";
import { MAIL_MERGE_TEXT_EMPHASIS } from "./MailMergeTextEmphasis";
import { useMailMergeAccessPrompts } from "./hooks/useMailMergeAccessPrompts";

interface SectionAction {
  label: string;
  run: () => unknown;
  /** Solid only for the very first grant; every later action is a quieter outlined button. */
  variant: "contained" | "outlined";
}

/**
 * The mail merge (Serienbrief) folder grant as one block of the user settings
 * (Benutzereinstellungen). Every state offers exactly one action — the way out of it — and whatever
 * explanation that action needs lives in the dialog it opens, not here.
 */
export function MailMergeAccessSection() {
  const { t } = useTranslation();
  const { access, folderDialog, requestFolderSelection, requestRestore, requestReset } = useMailMergeAccessPrompts();
  const { state, isLoading, isError, expectedFolderPath } = access;
  const [isManageOpen, setIsManageOpen] = useState(false);

  const folderValues = { expected: expectedFolderPath };

  /** Null where the state needs no alert: a grant that simply works says so in one quiet line. */
  const describeState = (): { severity: AlertColor; description: ReactNode } | null => {
    switch (state) {
      case MailMergeAccessState.Unsupported:
        return {
          severity: "info",
          description: (
            <Trans i18nKey="mailMerge.access.descriptions.unsupported" components={MAIL_MERGE_TEXT_EMPHASIS} />
          ),
        };
      case MailMergeAccessState.NotConfigured:
        return {
          severity: "info",
          description: (
            <Trans i18nKey="mailMerge.access.descriptions.notConfigured" components={MAIL_MERGE_TEXT_EMPHASIS} />
          ),
        };
      case MailMergeAccessState.NotGranted:
        return {
          severity: "info",
          description: (
            <Trans i18nKey="mailMerge.access.descriptions.notGranted" components={MAIL_MERGE_TEXT_EMPHASIS} />
          ),
        };
      case MailMergeAccessState.PermissionLost:
        return {
          severity: "error",
          description: (
            <Trans
              i18nKey="mailMerge.access.descriptions.permissionLost"
              values={folderValues}
              components={MAIL_MERGE_TEXT_EMPHASIS}
            />
          ),
        };
      case MailMergeAccessState.PermissionDenied:
        return {
          severity: "error",
          description: (
            <Trans
              i18nKey="mailMerge.access.descriptions.permissionDenied"
              values={folderValues}
              components={MAIL_MERGE_TEXT_EMPHASIS}
            />
          ),
        };
      case MailMergeAccessState.FolderMismatch:
        return {
          severity: "error",
          description: (
            <Trans
              i18nKey="mailMerge.access.descriptions.folderMismatch"
              values={folderValues}
              components={MAIL_MERGE_TEXT_EMPHASIS}
            />
          ),
        };
      case MailMergeAccessState.Ready:
        return null;
    }
  };

  // The managing dialog steps aside first: its two folder actions raise a dialog of their own, and
  // stacking those would bury the one the user is answering.
  const changeFolderFromManage = async () => {
    setIsManageOpen(false);
    await requestFolderSelection();
  };

  const resetFromManage = async () => {
    setIsManageOpen(false);
    await requestReset();
  };

  /** One action per state: the card offers the way out, the dialog it opens explains the situation. */
  const primaryAction = (): SectionAction | null => {
    switch (state) {
      case MailMergeAccessState.NotGranted:
        return { label: t("mailMerge.access.actions.grant"), run: requestFolderSelection, variant: "contained" };
      case MailMergeAccessState.PermissionLost:
        return { label: t("mailMerge.access.actions.restore"), run: requestRestore, variant: "outlined" };
      case MailMergeAccessState.FolderMismatch:
        return { label: t("mailMerge.access.actions.grant"), run: requestFolderSelection, variant: "outlined" };
      case MailMergeAccessState.Ready:
        return { label: t("mailMerge.access.actions.manage"), run: () => setIsManageOpen(true), variant: "outlined" };
      case MailMergeAccessState.Unsupported:
      case MailMergeAccessState.NotConfigured:
      case MailMergeAccessState.PermissionDenied:
        return null;
    }
  };

  const isResolved = !isLoading && !isError;
  const primary = primaryAction();
  const stateAlert = describeState();

  return (
    <Stack spacing={1.5} p={2} sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t("mailMerge.access.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("mailMerge.access.hint")}
          </Typography>
        </Stack>
        {isResolved && primary && (
          <Button variant={primary.variant} onClick={() => void primary.run()} sx={{ flexShrink: 0 }}>
            {primary.label}
          </Button>
        )}
      </Stack>

      {isLoading && (
        <Stack alignItems="center" py={1}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {isError && <Alert severity="error">{t("mailMerge.access.descriptions.loadFailed")}</Alert>}

      {isResolved && stateAlert && <Alert severity={stateAlert.severity}>{stateAlert.description}</Alert>}

      {folderDialog}

      {isManageOpen && state === MailMergeAccessState.Ready && expectedFolderPath !== null && (
        <MailMergeManageDialog
          folderPath={expectedFolderPath}
          onChangeFolder={() => void changeFolderFromManage()}
          onReset={() => void resetFromManage()}
          onClose={() => setIsManageOpen(false)}
        />
      )}
    </Stack>
  );
}
